import { useQuery } from '@tanstack/react-query';
import { GscSummarySection } from '../components/GscSummarySection';
import { SeoTrackingTable } from '../components/SeoTrackingTable';
import { fetchIndex } from '../api/seoTracking';

function NeedsResuggestBanner() {
  const { data } = useQuery({ queryKey: ['seo-tracking-index'], queryFn: fetchIndex, staleTime: 5 * 60 * 1000 });
  if (!data) return null;
  const total = data.pages.reduce((a, p) => a + (p.todos.needsResuggest ?? 0), 0);
  if (total === 0) return null;
  const pages = data.pages.filter((p) => (p.todos.needsResuggest ?? 0) > 0).length;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-orange-300 bg-orange-50 px-4 py-3">
      <span className="text-2xl leading-none">⚠</span>
      <div className="flex-1">
        <div className="text-sm font-semibold text-orange-900">
          {total} TODO{total === 1 ? '' : 's'} awaiting re-suggest across {pages} page{pages === 1 ? '' : 's'}
        </div>
        <div className="mt-0.5 text-xs text-orange-800">
          The applier rejected these TODOs (typically because the suggest agent wrote replacement text in the wrong language/script for the locale, or the change failed a smell-test).
          They&apos;ll be rewritten on the next Monday suggest cycle. Filter the table below by{' '}
          <em>&quot;Has needs re-suggest&quot;</em> to inspect them.
        </div>
      </div>
    </div>
  );
}

export function SeoGscPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">SEO &amp; GSC</h1>
        <p className="mt-1 text-sm text-gray-500">
          Per-page GSC + on-page tracking for xbot.ninja (blog + docs).
          Data is sourced from <code>seo-tracking/**/AUDIT.md</code>{' '}
          + <code>TODO.md</code> in the repo; the index is regenerated on
          every push to <code>seo-tracking/</code> and daily.
        </p>
      </div>

      {/* Needs-re-suggest warning banner (only renders when count > 0) */}
      <NeedsResuggestBanner />

      {/* Site-wide GSC overview (live Search Console read) */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Site-wide GSC overview</h2>
        <GscSummarySection />
      </section>

      {/* Per-page tracking table */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Per-page tracking</h2>
        <SeoTrackingTable />
      </section>
    </div>
  );
}
